import React, { useEffect, useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import { PictureAsPdf, RemoveRedEye, EditCalendar } from "@mui/icons-material";
import {
  fetchVendorAppointmentDetails,
  fetchVendorAppointmentList,
  fetchAppointmentDateQuantityMasterList,
  rescheduleVendorAppointment,
} from "Service/ApiService";
import { useLoading } from "context/LoadingContext";
import { useAlert } from "context/AlertContext";
import { BASE_URL } from "Service/ApiService";

const EWAY_BILL_BASE_URL = `${BASE_URL}vendor/uploads/asn_EwayBill/`;

const formatDateValue = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AppointmentListController = () => {
  const [appointmentRows, setAppointmentRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoadingState] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [openDetailsPopup, setOpenDetailsPopup] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentDetailRows, setAppointmentDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const { setLoading } = useLoading();
  const { showAlert } = useAlert();
  const vendorDetails = JSON.parse(localStorage.getItem("vendor"));
  const vendorCode = vendorDetails?.vendorCode || null;

  // Reschedule feature state
  const [openReschedulePopup, setOpenReschedulePopup] = useState(false);
  const [appointmentDateSlotMap, setAppointmentDateSlotMap] = useState({});
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState("");
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);

  const fetchAppointments = async () => {
    if (!vendorCode) {
      setAppointmentRows([]);
      setRowCount(0);
      return;
    }

    try {
      setLoading(true);
      setLoadingState(true);
      const response = await fetchVendorAppointmentList(
        vendorCode,
        paginationModel.page + 1,
        paginationModel.pageSize
      );

      if (response?.success === "true") {
        const rows = (response.asnAppointmentList || []).map((item, index) => ({
          id: item.asnAppointmentId,
          srNo: paginationModel.page * paginationModel.pageSize + index + 1,
          asnAppointmentId: item.asnAppointmentId,
          vendorCode: item.vendor_code || "-",
          vendorName: item.name || "-",
          vendorPoId: item.vendorPoId || "-",
          poNumber: item.poNumber || "-",
          appointmentDate: item.appointmentDate || "-",
          appointmentDateLabel: formatDateValue(item.appointmentDate),
          vendorDispatchQuantity: item.vendor_dispatch_quantity || "0.00",
          ewayBillPdf: item.ewayBillPdf || "",
          isGateEntrySubmit: item.isGateEntrySubmit || "-",
        }));

        setAppointmentRows(rows);
        setRowCount(Number(response.pagination?.totalRecords || rows.length || 0));
      } else {
        setAppointmentRows([]);
        setRowCount(0);
      }
    } catch (error) {
      setAppointmentRows([]);
      setRowCount(0);
      showAlert("Unable to fetch appointment list.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setLoading(false);
      setLoadingState(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [paginationModel, vendorCode]);

  const handleViewDetails = async (row) => {
    try {
      setSelectedAppointment(row);
      setAppointmentDetailRows([]);
      setOpenDetailsPopup(true);
      setDetailLoading(true);

      const response = await fetchVendorAppointmentDetails(row.asnAppointmentId);

      if (response?.success === "true") {
        const rows = (response.asnAppointmentDetailsList || []).map((item) => ({
          id: item.asnAppointmentDtlsId,
          materialCode: item.materialCode || "-",
          boxNumber: item.boxNumber || "-",
          brand: item.brand || "-",
          product: item.product || "-",
          materialDesc: item.materialDesc || "-",
          barcode: item.barcode || "-",
          quantity: item.quantity || "0.00",
        }));

        setAppointmentDetailRows(rows);
      } else {
        setAppointmentDetailRows([]);
        showAlert(response?.msg || "No appointment details found.", {
          icon: "info",
          title: "No Data",
        });
      }
    } catch (error) {
      setAppointmentDetailRows([]);
      showAlert("Unable to fetch appointment details.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetailsPopup = () => {
    setOpenDetailsPopup(false);
    setSelectedAppointment(null);
    setAppointmentDetailRows([]);
    setDetailLoading(false);
  };

  const handleOpenEwayBill = (fileName) => {
    if (!fileName || !String(fileName).trim()) {
      return;
    }

    const fileUrl = `${EWAY_BILL_BASE_URL}${encodeURIComponent(fileName)}`;
    const link = document.createElement("a");

    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ===================== Reschedule: calendar helper logic (reused from ASNStatusController) =====================

  const getMonthLabel = (dateValue) =>
    dateValue.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });

  const normalizeDateKey = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;
  };

  const todayDateKey = normalizeDateKey(new Date());

  const isPastDate = (dateKey) => {
    if (!dateKey) {
      return false;
    }

    return dateKey < todayDateKey;
  };

  const isCurrentOrFutureMonth = (monthDate) => {
    const today = new Date();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const selectedMonthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);

    return selectedMonthStart >= currentMonthStart;
  };

  const getCalendarDays = (monthDate) => {
    const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const days = [];

    for (let i = 0; i < startDay; i += 1) {
      days.push(null);
    }

    for (let day = 1; day <= totalDays; day += 1) {
      const dateValue = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
      days.push({
        day,
        dateKey: normalizeDateKey(dateValue),
      });
    }

    return days;
  };

  const handleCalendarMonthChange = (direction) => {
    setCurrentCalendarMonth((prev) => {
      const nextMonth = new Date(prev.getFullYear(), prev.getMonth() + direction, 1);
      return isCurrentOrFutureMonth(nextMonth) ? nextMonth : prev;
    });
  };

  const getSlotStatus = (dateKey) => {
    if (isPastDate(dateKey)) {
      return "Closed";
    }

    const slot = appointmentDateSlotMap[dateKey];

    if (!slot) {
      return "No Slot Available";
    }

    return Number(slot.quantityPercentExtra || 0) > 0 ? "Available" : "Fully Booked";
  };

  const isSlotDateAvailable = (dateKey) => getSlotStatus(dateKey) === "Available";

  const expandAppointmentDateSlots = (slotList = []) => {
    const expandedSlots = {};

    slotList.forEach((slot) => {
      if (slot?.datesMaster) {
        const normalizedDate = normalizeDateKey(slot.datesMaster);

        if (!normalizedDate) {
          return;
        }

        expandedSlots[normalizedDate] = {
          ...slot,
          quantity: Number(slot.quantity || 0),
          quantityPercentExtra: Number(slot.quantityPercentExtra || 0),
          dateKey: normalizedDate,
        };
        return;
      }

      if (slot?.fromDate && slot?.toDate) {
        const startDate = new Date(slot.fromDate);
        const endDate = new Date(slot.toDate);

        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
          return;
        }

        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dateKey = normalizeDateKey(currentDate);
          expandedSlots[dateKey] = {
            ...slot,
            quantity: Number(slot.quantity || 0),
            quantityPercentExtra: Number(slot.quantityPercentExtra || 0),
            dateKey,
          };
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });

    return expandedSlots;
  };

  const getAppointmentDateSlots = async () => {
    try {
      const response = await fetchAppointmentDateQuantityMasterList();

      if (response?.success === "true") {
        const expandedSlots = expandAppointmentDateSlots(
          response.appointmentDateQntyMasterList || []
        );

        setAppointmentDateSlotMap(expandedSlots);
      } else {
        setAppointmentDateSlotMap({});
      }
    } catch (error) {
      setAppointmentDateSlotMap({});
    }
  };

  // ===================== Reschedule: dialog open/close & submit =====================

  const handleOpenReschedule = async (row) => {
    setSelectedAppointment(row);
    setSelectedCalendarDate("");
    setOpenReschedulePopup(true);

    const existingAppointmentDate = new Date(row?.appointmentDate);
    const initialMonth = Number.isNaN(existingAppointmentDate.getTime())
      ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      : new Date(existingAppointmentDate.getFullYear(), existingAppointmentDate.getMonth(), 1);

    setCurrentCalendarMonth(
      isCurrentOrFutureMonth(initialMonth)
        ? initialMonth
        : new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );

    try {
      setRescheduleLoading(true);
      await getAppointmentDateSlots();
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleCloseReschedulePopup = () => {
    setOpenReschedulePopup(false);
    setSelectedAppointment(null);
    setSelectedCalendarDate("");
    setRescheduleLoading(false);
    setRescheduleSubmitting(false);
  };

  const handleRescheduleDateSelect = (dateKey) => {
    if (!isSlotDateAvailable(dateKey)) {
      return;
    }

    if (dateKey === normalizeDateKey(selectedAppointment?.appointmentDate)) {
      return;
    }

    setSelectedCalendarDate(dateKey);
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAppointment?.asnAppointmentId || !selectedCalendarDate) {
      return;
    }

    try {
      setRescheduleSubmitting(true);
      const response = await rescheduleVendorAppointment({
        asnAppointmentId: selectedAppointment.asnAppointmentId,
        rescheduleAppointmentDate: selectedCalendarDate,
      });

      if (response?.success === "true") {
        showAlert(response?.msg || "Appointment rescheduled successfully.", {
          icon: "success",
          title: "Success",
        });
        handleCloseReschedulePopup();
        await fetchAppointments();
      } else {
        showAlert(response?.msg || "Unable to reschedule appointment.", {
          icon: "error",
          title: "Error",
        });
      }
    } catch (error) {
      showAlert("Unable to reschedule appointment.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setRescheduleSubmitting(false);
    }
  };

  const columns = [
    { field: "srNo", headerName: "Sr No", flex: 0.7, minWidth: 90 },
    { field: "poNumber", headerName: "PO Number", flex: 1, minWidth: 140 },
    // { field: "vendorCode", headerName: "Vendor Code", flex: 1, minWidth: 130 },
    // { field: "vendorName", headerName: "Vendor Name", flex: 1.4, minWidth: 180 },
    { field: "appointmentDateLabel", headerName: "Appointment Date", flex: 1, minWidth: 150 },
    {
      field: "vendorDispatchQuantity",
      headerName: "Dispatch Qty",
      flex: 0.9,
      minWidth: 130,
    },
    { field: "isGateEntrySubmit", headerName: "Gate Entry", flex: 0.9, minWidth: 120 },
    {
      field: "ewayBillPdf",
      headerName: "E-way Bill PDF",
      minWidth: 130,
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasEwayBill = Boolean(params.row.ewayBillPdf && params.row.ewayBillPdf.trim());

        return (
          <Button
            disabled={!hasEwayBill}
            onClick={(event) => {
              event.stopPropagation();
              if (hasEwayBill) {
                handleOpenEwayBill(params.row.ewayBillPdf);
              }
            }}
            startIcon={<PictureAsPdf sx={{ fontSize: "2rem !important" }} />}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="View Details">
            <IconButton color="error" size="small" onClick={() => handleViewDetails(params.row)}>
              <RemoveRedEye fontSize="small" />
            </IconButton>
          </Tooltip>
          {String(params.row.isGateEntrySubmit).toLowerCase() === "no" && (
            <Tooltip title="Reschedule Appointment">
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleOpenReschedule(params.row)}
              >
                <EditCalendar fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const detailColumns = [
    { field: "materialCode", headerName: "Material Code", flex: 1.2, minWidth: 160 },
    { field: "boxNumber", headerName: "Box Number", flex: 0.8, minWidth: 110 },
    { field: "brand", headerName: "Brand", flex: 1, minWidth: 120 },
    { field: "product", headerName: "Product", flex: 1, minWidth: 120 },
    { field: "materialDesc", headerName: "Material Desc", flex: 1.6, minWidth: 220 },
    { field: "barcode", headerName: "Barcode", flex: 1.2, minWidth: 170 },
    { field: "quantity", headerName: "Quantity", flex: 0.8, minWidth: 100 },
  ];

  const totalBookedQuantity = appointmentDetailRows.reduce(
    (total, row) => total + (parseFloat(row.quantity) || 0),
    0
  );

  const getAvailableQuantity = (dateKey) => {
    return Number(appointmentDateSlotMap[dateKey]?.quantityPercentExtra || 0);
  };

  return {
    loading,
    appointmentRows,
    rowCount,
    paginationModel,
    setPaginationModel,
    columns,
    openDetailsPopup,
    handleCloseDetailsPopup,
    selectedAppointment,
    appointmentDetailRows,
    detailColumns,
    detailLoading,

    // Reschedule feature
    openReschedulePopup,
    handleCloseReschedulePopup,
    currentCalendarMonth,
    handleCalendarMonthChange,
    getCalendarDays,
    getMonthLabel,
    isPastDate,
    isCurrentOrFutureMonth,
    getSlotStatus,
    getAvailableQuantity,
    isSlotDateAvailable,
    normalizeDateKey,
    selectedCalendarDate,
    handleRescheduleDateSelect,
    handleRescheduleSubmit,
    rescheduleLoading,
    rescheduleSubmitting,
    formatDateValue,
    totalBookedQuantity,
  };
};

export default AppointmentListController;